import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  inteligenciaApi, generacionApi, guiasApi,
  type Tema, type ContenidoItem, type Guia,
} from '../lib/api';
import { Toast, type ToastState } from '../components/Toast';

const TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  copper: { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  sage:   { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  amber:  { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  info:   { soft: 'var(--info-soft)',   ink: 'var(--info)',       solid: 'var(--info)',   line: '#cbd9e2'            },
};

const CATS: Record<string, { label: string; tone: string }> = {
  'ANSIEDAD':            { label: 'Ansiedad',  tone: 'copper' },
  'DEPRESIÓN':           { label: 'Depresión', tone: 'info'   },
  'AUTOESTIMA':          { label: 'Autoestima',tone: 'sage'   },
  'RELACIONES':          { label: 'Relaciones',tone: 'amber'  },
  'ESTRÉS':              { label: 'Estrés',    tone: 'amber'  },
  'DUELO':               { label: 'Duelo',     tone: 'sage'   },
  'TRAUMA':              { label: 'Trauma',    tone: 'info'   },
  'MINDFULNESS':         { label: 'Mindfulness',tone:'sage'   },
  'LÍMITES':             { label: 'Límites',   tone: 'copper' },
  'CRECIMIENTO PERSONAL':{ label: 'Crecim.',   tone: 'amber'  },
};

function CatTag({ cat, small }: { cat: string | null; small?: boolean }) {
  const c = cat ? CATS[cat] : null;
  if (!c) return <span className="tag">{cat ?? '—'}</span>;
  const t = TONE[c.tone] ?? TONE.copper;
  return (
    <span className="tag" style={{ background: t.soft, color: t.ink, borderColor: t.line, height: small ? 21 : 23 }}>
      <span className="dot" style={{ background: t.solid }} />
      {c.label}
    </span>
  );
}

function Meter({ score }: { score: number }) {
  return (
    <span className="meter">
      <span className="meter-bar"><span className="meter-fill" style={{ width: score + '%' }} /></span>
      <span className="meter-val" style={{ color: score >= 88 ? 'var(--copper-ink)' : score >= 78 ? 'var(--sage-ink)' : 'var(--ink-2)' }}>{score}</span>
    </span>
  );
}

function relDate(iso: string): string {
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  if (days < 14) return 'hace 1 sem';
  return `hace ${Math.floor(days / 7)} sem`;
}

function todayLabel(): string {
  const d = new Date();
  const days = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
  const months = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  return `${days[d.getDay()].charAt(0).toUpperCase() + days[d.getDay()].slice(1)} ${d.getDate()} de ${months[d.getMonth()]}, ${d.getFullYear()}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [topics,  setTopics]  = useState<Tema[]>([]);
  const [content, setContent] = useState<ContenidoItem[]>([]);
  const [guides,  setGuides]  = useState<Guia[]>([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((msg: string, type: ToastState['type']) => setToast({ message: msg, type }), []);

  useEffect(() => {
    Promise.all([
      inteligenciaApi.getTemas(),
      generacionApi.getContenido(),
      guiasApi.getGuias().catch(() => ({ guias: [] as Guia[], total: 0 })),
    ])
      .then(([t, c, g]) => { setTopics(t.temas); setContent(c.items); setGuides(g.guias); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleGenerarTemas = async () => {
    setGenerando(true);
    try {
      const r = await inteligenciaApi.generarTemas();
      showToast(`${r.insertados} temas generados`, 'success');
      setTopics(prev => [...prev]); // trigger re-count
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error', 'error');
    } finally {
      setGenerando(false);
    }
  };

  const pend   = content.filter(c => c.status === 'borrador');
  const aprob  = content.filter(c => c.status === 'aprobado');
  const avail  = topics.filter(t => !t.usado);
  const topTopics = [...topics].sort((a, b) => (b.score ?? 0) - (a.score ?? 0)).slice(0, 4);
  const activeGuides = guides.filter(g => g.activa);

  const actions = [
    { ic: SparkIco,  tone: 'copper', t: 'Generar temas',    s: 'Con IA · análisis LATAM',    go: () => navigate('/inteligencia')  },
    { ic: LayersIco, tone: 'sage',   t: 'Crear contenido',  s: pend.length + ' en revisión', go: () => navigate('/generacion')   },
    { ic: UploadIco, tone: 'info',   t: 'Subir guía PDF',   s: 'Vincula por categoría',      go: () => navigate('/guias')        },
    { ic: CheckIco,  tone: 'amber',  t: 'Revisar pendientes',s: pend.length + ' piezas',      go: () => navigate('/generacion')   },
  ];

  return (
    <div className="page">
      {/* Header */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 26 }}>
        <div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 3 }}>{todayLabel()}</div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 30, margin: 0, letterSpacing: '-0.01em' }}>
            Buenos días 🌿
          </h1>
          {!loading && (
            <p className="muted" style={{ margin: '8px 0 0', fontSize: 14.5 }}>
              Tienes{' '}
              <b style={{ color: 'var(--accent-ink)' }}>{pend.length} piezas</b>{' '}
              esperando revisión y{' '}
              <b style={{ color: 'var(--sage-ink)' }}>{avail.length} temas nuevos</b>{' '}
              listos para producir.
            </p>
          )}
        </div>
        <button className="btn btn-primary" onClick={handleGenerarTemas} disabled={generando} style={{ flexShrink: 0 }}>
          {generando ? <span className="spinner" style={{ borderColor: 'rgba(255,255,255,.3)', borderTopColor: '#fff' }} /> : <SparkIco />}
          {generando ? 'Generando…' : 'Generar temas'}
        </button>
      </div>

      {loading && <div className="loading-center"><span className="spinner" /> Cargando métricas…</div>}

      {!loading && (
        <>
          {/* Stat cards */}
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 'var(--gap)' }}>
            {[
              { icon: SparkIco,  tone: 'copper', num: avail.length,        label: 'Temas disponibles',    delta: '2 generados hoy',         dir: 'up'  },
              { icon: LayersIco, tone: 'amber',  num: pend.length,         label: 'Pendientes de aprobar',delta: aprob.length + ' ya aprobadas', dir: 'flat'},
              { icon: MegaIco,   tone: 'sage',   num: content.filter(c=>c.status==='publicado').length, label:'Publicados este mes', delta:'+3 vs. mes previo', dir:'up'},
              { icon: BookIco,   tone: 'info',   num: activeGuides.length, label: 'Guías activas',        delta: guides.length + ' en catálogo',  dir:'flat'},
            ].map(({ icon: Icon, tone, num, label, delta, dir }) => {
              const t = TONE[tone];
              const DI = dir === 'up' ? ArrowUpIco : null;
              return (
                <div key={label} className="card stat fade-up">
                  <div className="stat-top">
                    <span className="stat-ico" style={{ background: t.soft, color: t.solid }}><Icon /></span>
                  </div>
                  <div className="stat-num tabular">{num}</div>
                  <div className="stat-label">{label}</div>
                  <div className={`stat-delta ${dir}`}>
                    {DI && <DI size={13} />} {delta}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="sec-head" style={{ marginBottom: 16 }}>
            <h2>Acciones rápidas</h2>
            <span className="line" />
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 30 }}>
            {actions.map(a => {
              const t = TONE[a.tone]; const Icon = a.ic;
              return (
                <button key={a.t} className="card card-pad" onClick={a.go}
                  style={{ textAlign: 'left', cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'center', transition: 'border-color .15s, transform .08s, box-shadow .15s', background: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = t.line; e.currentTarget.style.boxShadow = 'var(--shadow)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow-xs)'; e.currentTarget.style.transform = 'none'; }}>
                  <span className="stat-ico" style={{ background: t.soft, color: t.solid, width: 42, height: 42, flex: '0 0 42px' }}><Icon size={20} /></span>
                  <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap' }}>{a.t}</span>
                    <span className="muted" style={{ fontSize: 12.5 }}>{a.s}</span>
                  </span>
                  <span style={{ color: 'var(--muted)' }}><ChevRIco size={18} /></span>
                </button>
              );
            })}
          </div>

          {/* Bottom two columns */}
          <div className="grid" style={{ gridTemplateColumns: '1.35fr 1fr', alignItems: 'start' }}>
            {/* Pending review */}
            <div className="card">
              <div className="row card-pad" style={{ justifyContent: 'space-between', paddingBottom: 14 }}>
                <div className="row" style={{ gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: 16, whiteSpace: 'nowrap' }}>Pendiente de aprobar</h3>
                  <span className="tag">{pend.length}</span>
                </div>
                <button onClick={() => navigate('/generacion')} style={{ fontSize: 13, color: 'var(--accent-ink)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Ver todo</button>
              </div>
              <hr className="hr" />
              {pend.length === 0 && (
                <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--muted)' }}>
                  <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}><CheckIco size={24} /></span>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Todo al día</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>No hay piezas pendientes de revisión.</div>
                </div>
              )}
              {pend.map(c => (
                <button key={c.id} onClick={() => navigate('/generacion')}
                  className="row" style={{ width: '100%', textAlign: 'left', gap: 13, padding: '14px 22px', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background .12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <span className="stat-ico" style={{ background: 'var(--accent-soft)', color: 'var(--accent)', width: 38, height: 38, flex: '0 0 38px' }}><LayersIco size={18} /></span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.titulo}</span>
                    <span className="row" style={{ gap: 8, marginTop: 5 }}>
                      <span className="tag" style={{ height: 21, fontSize: 11 }}>{c.tipo}</span>
                      <span className="muted" style={{ fontSize: 12 }}>{relDate(c.created_at)}</span>
                    </span>
                  </span>
                  <span style={{ color: 'var(--muted)' }}><ChevRIco size={17} /></span>
                </button>
              ))}
            </div>

            {/* Mayor potencial */}
            <div className="card card-pad">
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: 16, whiteSpace: 'nowrap' }}>Mayor potencial</h3>
                <span style={{ color: 'var(--copper)' }}><TrendIco size={18} /></span>
              </div>
              <p className="muted" style={{ fontSize: 12.5, margin: '0 0 14px' }}>Temas detectados por la inteligencia</p>
              <div className="col" style={{ gap: 14 }}>
                {topTopics.map(t => (
                  <div key={t.id}>
                    <div className="row" style={{ justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>{t.tema}</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <CatTag cat={t.categoria} small />
                      <Meter score={t.score ?? 0} />
                    </div>
                  </div>
                ))}
                {topTopics.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Sin temas generados aún.</div>}
              </div>
              <button className="btn btn-soft" onClick={() => navigate('/inteligencia')} style={{ width: '100%', marginTop: 18, justifyContent: 'center' }}>
                <SparkIco size={16} /> Ir a Inteligencia
              </button>
            </div>
          </div>
        </>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function SparkIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M12 3l1.8 4.7L18 9.5l-4.2 1.8L12 16l-1.8-4.7L6 9.5l4.2-1.8z"/><path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></Svg>; }
function LayersIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M12 3l8 4.5-8 4.5-8-4.5z"/><path d="M4 12l8 4.5L20 12"/><path d="M4 16.5L12 21l8-4.5"/></Svg>; }
function MegaIco({ size = 18 }: { size?: number })    { return <Svg size={size}><path d="M3 11v2a1 1 0 0 0 1 1h2l8 5V5L6 10H4a1 1 0 0 0-1 1z"/><path d="M18 8a4 4 0 0 1 0 8"/></Svg>; }
function BookIco({ size = 18 }: { size?: number })    { return <Svg size={size}><path d="M5 4h9a3 3 0 0 1 3 3v13H8a3 3 0 0 0-3 3z" transform="translate(1 0)"/><path d="M4 4a2 2 0 0 0-2 2v13a3 3 0 0 1 3-3h1"/></Svg>; }
function UploadIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M12 16V4m-5 5 5-5 5 5"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></Svg>; }
function CheckIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M5 12.5l4.5 4.5L19 6.5"/></Svg>; }
function TrendIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M3 16l5-5 4 4 8-8"/><path d="M14 7h6v6"/></Svg>; }
function ArrowUpIco({ size = 18 }: { size?: number }) { return <Svg size={size}><path d="M12 19V5M6 11l6-6 6 6"/></Svg>; }
function ChevRIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M9 5l7 7-7 7"/></Svg>; }
